import { useState, useEffect } from 'react';
import { ExternalLink, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { fetchJurisdictionArticles, type Article } from '@/services/scoreService';

interface ArticleBrowserProps {
  jurisdictionCode: string;
  jurisdictionName: string;
}

const signalTypeLabels: Record<string, string> = {
  enforcement: 'Enforcement',
  regulatory_change: 'Regulatory Change',
  rhetoric: 'Rhetoric',
  market_stability: 'Market Stability',
};

const signalTypeColors: Record<string, string> = {
  enforcement: '#c9524a',
  regulatory_change: '#3B82F6',
  rhetoric: '#e6a23c',
  market_stability: '#3d9970',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(dateString);
}

export default function ArticleBrowser({ jurisdictionCode, jurisdictionName }: ArticleBrowserProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<{ signalType: string; days: number }>({
    signalType: 'all',
    days: 30,
  });

  // Load articles when opened or filters change
  useEffect(() => {
    if (!isOpen) return;

    const loadArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchJurisdictionArticles(jurisdictionCode, {
          signalType: filters.signalType === 'all' ? undefined : filters.signalType,
          days: filters.days,
        });
        setArticles(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, [jurisdictionCode, filters, isOpen]);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-text-primary flex items-center gap-2">
                Source Articles
                {articles.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {articles.length}
                  </Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-text-secondary" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-text-secondary" />
                )}
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-2">
            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-text-tertiary" />
              <Select
                value={filters.signalType}
                onValueChange={(value) => setFilters((f) => ({ ...f, signalType: value }))}
              >
                <SelectTrigger className="h-8 text-xs w-[140px]">
                  <SelectValue placeholder="Signal type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="enforcement">Enforcement</SelectItem>
                  <SelectItem value="regulatory_change">Regulatory Change</SelectItem>
                  <SelectItem value="rhetoric">Rhetoric</SelectItem>
                  <SelectItem value="market_stability">Market Stability</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.days.toString()}
                onValueChange={(value) => setFilters((f) => ({ ...f, days: parseInt(value) }))}
              >
                <SelectTrigger className="h-8 text-xs w-[100px]">
                  <SelectValue placeholder="Time period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Loading state */}
            {loading && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className="text-center py-4 text-text-secondary text-sm">
                Unable to load articles
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && articles.length === 0 && (
              <div className="text-center py-4 text-text-secondary text-sm">
                No articles found for the selected filters
              </div>
            )}

            {/* Article list */}
            {!loading && !error && articles.length > 0 && (
              <div className="space-y-3">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="p-3 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-text-primary hover:underline line-clamp-2 flex items-center gap-1"
                        >
                          {article.title}
                          <ExternalLink className="h-3 w-3 flex-shrink-0 text-text-tertiary" />
                        </a>
                        <div className="flex items-center gap-2 mt-1 text-xs text-text-tertiary">
                          <span>{article.source}</span>
                          <span>•</span>
                          <span>{getRelativeTime(article.published_at)}</span>
                        </div>
                      </div>
                      {article.signal && (
                        <Badge
                          variant="outline"
                          className="text-xs flex-shrink-0"
                          style={{
                            borderColor: signalTypeColors[article.signal.type] || '#888',
                            color: signalTypeColors[article.signal.type] || '#888',
                          }}
                        >
                          {signalTypeLabels[article.signal.type] || article.signal.type}
                        </Badge>
                      )}
                    </div>
                    {article.signal?.explanation && (
                      <p className="mt-2 text-xs text-text-secondary line-clamp-2">
                        {article.signal.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
